import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../client';
import { useAuth } from '../../contexts/AuthContext';
import type {
  Team,
  TeamInviteDetails,
  CreateTeamData,
  UpdateTeamData,
  InviteMemberData,
  UpdateMemberRoleData,
} from '../../types';

/**
 * Hook to get all teams for the current user
 */
export function useTeams() {
  return useQuery({
    queryKey: ['teams'],
    queryFn: async () => {
      const response = await apiClient.get<{ data: Team[] }>('/api/v1/teams');
      return response.data.data;
    },
  });
}

/**
 * Hook to get a single team with members and invites
 */
export function useTeam(teamId: number | undefined) {
  return useQuery({
    queryKey: ['team', teamId],
    queryFn: async () => {
      const response = await apiClient.get<Team>(`/api/v1/teams/${teamId}`);
      return response.data;
    },
    enabled: !!teamId,
  });
}

/**
 * Hook to get invite details by token (public)
 */
export function useInviteDetails(token: string | undefined) {
  return useQuery({
    queryKey: ['invite', token],
    queryFn: async () => {
      const response = await apiClient.get<{ invite: TeamInviteDetails }>(`/api/v1/team-invites/${token}`);
      return response.data.invite;
    },
    enabled: !!token,
    retry: false,
  });
}

/**
 * Hook to create a new team
 */
export function useCreateTeam() {
  const { refreshUser } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateTeamData) => {
      const response = await apiClient.post<{ team: Team }>('/api/v1/teams', data);
      return response.data.team;
    },
    onSuccess: async () => {
      await refreshUser();
      queryClient.invalidateQueries({ queryKey: ['teams'] });
    },
  });
}

/**
 * Hook to update a team
 */
export function useUpdateTeam() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ teamId, data }: { teamId: number; data: UpdateTeamData }) => {
      const response = await apiClient.patch<{ team: Team }>(`/api/v1/teams/${teamId}`, data);
      return response.data.team;
    },
    onSuccess: (_, { teamId }) => {
      queryClient.invalidateQueries({ queryKey: ['teams'] });
      queryClient.invalidateQueries({ queryKey: ['team', teamId] });
    },
  });
}

/**
 * Hook to delete a team
 */
export function useDeleteTeam() {
  const { refreshUser } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (teamId: number) => {
      const response = await apiClient.delete(`/api/v1/teams/${teamId}`);
      return response.data;
    },
    onSuccess: async () => {
      await refreshUser();
      queryClient.invalidateQueries({ queryKey: ['teams'] });
    },
  });
}

/**
 * Hook to switch to a different team
 */
export function useSwitchTeam() {
  const { refreshUser } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (teamId: number) => {
      const response = await apiClient.post<{ team: Team }>(`/api/v1/teams/${teamId}/switch`);
      return response.data.team;
    },
    onSuccess: async () => {
      await refreshUser();
      queryClient.invalidateQueries({ queryKey: ['teams'] });
    },
  });
}

/**
 * Hook to invite a member to a team
 */
export function useInviteMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ teamId, data }: { teamId: number; data: InviteMemberData }) => {
      const response = await apiClient.post(`/api/v1/teams/${teamId}/invites`, data);
      return response.data;
    },
    onSuccess: (_, { teamId }) => {
      queryClient.invalidateQueries({ queryKey: ['team', teamId] });
    },
  });
}

/**
 * Hook to cancel an invite
 */
export function useCancelInvite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ teamId, inviteId }: { teamId: number; inviteId: number }) => {
      const response = await apiClient.delete(`/api/v1/teams/${teamId}/invites/${inviteId}`);
      return response.data;
    },
    onSuccess: (_, { teamId }) => {
      queryClient.invalidateQueries({ queryKey: ['team', teamId] });
    },
  });
}

/**
 * Hook to resend an invite
 */
export function useResendInvite() {
  return useMutation({
    mutationFn: async ({ teamId, inviteId }: { teamId: number; inviteId: number }) => {
      const response = await apiClient.post(`/api/v1/teams/${teamId}/invites/${inviteId}/resend`);
      return response.data;
    },
  });
}

/**
 * Hook to update a member's role
 */
export function useUpdateMemberRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ teamId, userId, data }: { teamId: number; userId: number; data: UpdateMemberRoleData }) => {
      const response = await apiClient.patch(`/api/v1/teams/${teamId}/members/${userId}`, data);
      return response.data;
    },
    onSuccess: (_, { teamId }) => {
      queryClient.invalidateQueries({ queryKey: ['team', teamId] });
    },
  });
}

/**
 * Hook to remove a member from a team
 */
export function useRemoveMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ teamId, userId }: { teamId: number; userId: number }) => {
      const response = await apiClient.delete(`/api/v1/teams/${teamId}/members/${userId}`);
      return response.data;
    },
    onSuccess: (_, { teamId }) => {
      queryClient.invalidateQueries({ queryKey: ['team', teamId] });
    },
  });
}

/**
 * Hook to leave a team
 */
export function useLeaveTeam() {
  const { refreshUser } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (teamId: number) => {
      const response = await apiClient.post(`/api/v1/teams/${teamId}/leave`);
      return response.data;
    },
    onSuccess: async () => {
      await refreshUser();
      queryClient.invalidateQueries({ queryKey: ['teams'] });
    },
  });
}

/**
 * Hook to transfer team ownership
 */
export function useTransferOwnership() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ teamId, userId }: { teamId: number; userId: number }) => {
      const response = await apiClient.post<{ team: Team }>(`/api/v1/teams/${teamId}/transfer/${userId}`);
      return response.data.team;
    },
    onSuccess: (_, { teamId }) => {
      queryClient.invalidateQueries({ queryKey: ['teams'] });
      queryClient.invalidateQueries({ queryKey: ['team', teamId] });
    },
  });
}

/**
 * Hook to accept an invite
 */
export function useAcceptInvite() {
  const { refreshUser } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (token: string) => {
      const response = await apiClient.post<{ team: Team }>(`/api/v1/team-invites/${token}/accept`);
      return response.data.team;
    },
    onSuccess: async () => {
      await refreshUser();
      queryClient.invalidateQueries({ queryKey: ['teams'] });
    },
  });
}

/**
 * Hook to decline an invite
 */
export function useDeclineInvite() {
  return useMutation({
    mutationFn: async (token: string) => {
      const response = await apiClient.post(`/api/v1/team-invites/${token}/decline`);
      return response.data;
    },
  });
}
